import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import type { ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { queryOptions, useQuery } from '@tanstack/react-query'
import type { AxiosInstance, AxiosResponse } from 'axios'

export type TicketNoteDirection = 'ingresso' | 'uscita' | 'interna' | string

export type TicketNoteTicketContext = Pick<ticket, 'id' | 'ded_Dis' | 'attachmentsFolderUrl'>

export type TicketNoteResponseDto = {
  noteId: number
  ticketId: string
  tag: string
  storedContentFormat: string
  origin: string
  authorType: string
  direction: TicketNoteDirection
  authorDisplayName?: string | null
  authorEmail?: string | null
  visibleToCustomer: boolean
  sentCompatibility: boolean
  isClosureNote: boolean
  createdAtUtc: string
  contentMarkdown?: string | null
  contentHtml?: string | null
}

export type TicketNoteUpsertPayload = {
  ticketRecordId: number
  noteId?: number | null
  content: string
  contentFormat?: 'html' | 'markdown'
  send?: boolean
  tag?: string | null
  attachments?: File[]
}

export type TicketNoteViewModel = {
  id: number
  ticketId: string
  ticketRecordId: number
  tag: string | null
  testo: string | null
  data: string | null
  storedContentFormat: string
  origin: string
  authorType: string
  direction: TicketNoteDirection
  authorDisplayName?: string | null
  authorEmail?: string | null
  visibleToCustomer: boolean
  sentCompatibility: boolean
  chiusura: boolean
  ticket?: TicketNoteTicketContext | null
}

const isLegacyCustomerOpeningNote = (note: TicketNoteViewModel) => {
  const normalizedBody = (note.testo ?? '').toLowerCase()
  return (
    note.authorType === 'sistema' &&
    note.direction === 'interna' &&
    (normalizedBody.includes('richiesta assistenza web') || normalizedBody.includes('support request'))
  )
}

export const getTicketNoteQueryKey = (ticketRecordId?: number | null) => ['ticket-notes', ticketRecordId]

export const buildTicketNoteContext = (
  ticketRecord: TicketNoteTicketContext | null | undefined,
): TicketNoteTicketContext | null => {
  if (typeof ticketRecord?.id !== 'number' || !ticketRecord?.ded_Dis) {
    return null
  }

  return {
    id: ticketRecord.id,
    ded_Dis: ticketRecord.ded_Dis,
    attachmentsFolderUrl: ticketRecord.attachmentsFolderUrl,
  }
}

export const createTicketNoteDraft = (
  ticketContext: TicketNoteTicketContext | null | undefined,
  note: Partial<TicketNoteViewModel> = {},
): TicketNoteViewModel => ({
  id: note.id ?? 0,
  ticketId: note.ticketId ?? ticketContext?.ded_Dis ?? '',
  ticketRecordId: note.ticketRecordId ?? ticketContext?.id ?? 0,
  tag: note.tag ?? null,
  testo: note.testo ?? null,
  data: note.data ?? null,
  storedContentFormat: note.storedContentFormat ?? 'html',
  origin: note.origin ?? 'tecnico',
  authorType: note.authorType ?? 'tecnico',
  direction: note.direction ?? 'interna',
  authorDisplayName: note.authorDisplayName ?? null,
  authorEmail: note.authorEmail ?? null,
  visibleToCustomer: note.visibleToCustomer ?? false,
  sentCompatibility: note.sentCompatibility ?? false,
  chiusura: note.chiusura ?? false,
  ticket: ticketContext ?? null,
})

export const canEditTicketNote = (note: TicketNoteViewModel | null | undefined, currentUserName?: string | null) => {
  if (!note) return false
  if (note.id <= 0) {
    return (
      note.chiusura !== true &&
      note.visibleToCustomer !== true &&
      note.sentCompatibility !== true &&
      note.direction === 'interna' &&
      note.authorType === 'tecnico'
    )
  }
  if (note.chiusura || note.visibleToCustomer || note.sentCompatibility) return false
  if (note.direction !== 'interna') return false
  if (note.authorType !== 'tecnico') return false
  if (!note.authorDisplayName || !currentUserName) return false
  return note.authorDisplayName === currentUserName
}

export const getTicketNoteAuthorLabel = (note: TicketNoteViewModel) => {
  const displayName = note.authorDisplayName?.trim()
  const authorEmail = note.authorEmail?.trim()
  const isCustomerNote =
    note.direction === 'ingresso' || note.authorType === 'cliente' || isLegacyCustomerOpeningNote(note)
  const normalizedDisplayName = isCustomerNote && displayName === 'Sistema QS' ? null : displayName

  if (authorEmail) {
    if (!normalizedDisplayName || normalizedDisplayName === authorEmail) {
      return `<${authorEmail}>`
    }

    return `${normalizedDisplayName} <${authorEmail}>`
  }

  if (normalizedDisplayName) {
    return normalizedDisplayName
  }

  if (isCustomerNote) {
    return 'Autore sconosciuto'
  }

  return 'Sistema QS'
}

const mapTicketNote = (
  ticketContext: TicketNoteTicketContext | null | undefined,
  note: TicketNoteResponseDto,
): TicketNoteViewModel => ({
  id: note.noteId,
  ticketId: note.ticketId,
  ticketRecordId: ticketContext?.id ?? 0,
  tag: note.tag,
  testo: note.contentHtml ?? '',
  data: note.createdAtUtc,
  storedContentFormat: note.storedContentFormat,
  origin: note.origin,
  authorType: note.authorType,
  direction: note.direction,
  authorDisplayName: note.authorDisplayName ?? null,
  authorEmail: note.authorEmail ?? null,
  visibleToCustomer: note.visibleToCustomer,
  sentCompatibility: note.sentCompatibility,
  chiusura: note.isClosureNote,
  ticket: ticketContext ?? null,
})

export const fetchTicketNotes = async (
  client: AxiosInstance,
  ticketContext: TicketNoteTicketContext | null | undefined,
  signal?: AbortSignal,
) => {
  const normalizedTicketContext = buildTicketNoteContext(ticketContext)
  if (typeof normalizedTicketContext?.id !== 'number') {
    return []
  }

  const response = await client.get<TicketNoteResponseDto[]>(`/api/tickets/${normalizedTicketContext.id}/notes`, {
    params: { format: 'html' },
    signal,
  })

  return response.data
    .map((note) => mapTicketNote(normalizedTicketContext, note))
    .sort((noteA, noteB) => new Date(noteB.data ?? 0).getTime() - new Date(noteA.data ?? 0).getTime())
}

export const getTicketNotesQueryOptions = (
  client: AxiosInstance,
  ticketContext: TicketNoteTicketContext | null | undefined,
) => {
  const normalizedTicketContext = buildTicketNoteContext(ticketContext)

  return queryOptions({
    queryKey: getTicketNoteQueryKey(normalizedTicketContext?.id),
    enabled: typeof normalizedTicketContext?.id === 'number',
    queryFn: ({ signal }) => fetchTicketNotes(client, normalizedTicketContext, signal),
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

export const useTicketNotes = (ticketContext: TicketNoteTicketContext | null | undefined) => {
  const { client } = useQsAdminApiManager()

  return useQuery(getTicketNotesQueryOptions(client, ticketContext))
}

export const buildTicketNoteUpsertFormData = (
  payload: TicketNoteUpsertPayload,
  options: {
    includeRouteFields?: boolean
  } = {},
) => {
  const formData = new FormData()
  formData.append('content', payload.content)
  formData.append('contentFormat', payload.contentFormat ?? 'html')
  formData.append('send', String(!!payload.send))

  if (payload.tag) {
    formData.append('tag', payload.tag)
  }

  payload.attachments?.forEach((file) => {
    formData.append('attachments', file)
  })

  if (options.includeRouteFields) {
    formData.append('ticketId', String(payload.ticketRecordId))
    formData.append('noteId', String(payload.noteId ?? 0))
  }

  return formData
}

export const upsertTicketNote = (
  client: AxiosInstance,
  payload: TicketNoteUpsertPayload,
): Promise<AxiosResponse<TicketNoteResponseDto>> => {
  const formData = buildTicketNoteUpsertFormData(payload)

  if (payload.noteId && payload.noteId > 0) {
    return client.put<TicketNoteResponseDto, AxiosResponse<TicketNoteResponseDto>>(
      `/api/tickets/${payload.ticketRecordId}/notes/${payload.noteId}`,
      formData,
    )
  }

  return client.post<TicketNoteResponseDto, AxiosResponse<TicketNoteResponseDto>>(
    `/api/tickets/${payload.ticketRecordId}/notes`,
    formData,
  )
}
